import { useState } from 'react';
import { Skeleton } from '../../components/Skeleton';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { StoreDto } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Section } from '../../components/Section';
import { AddStoreForm } from '../../components/stores/AddStoreForm';
import { DefaultProductsSection } from '../../components/stores/DefaultProductsSection';
import { FavouriteButton } from '../../components/stores/FavouriteButton';
import { departmentOrderSummary, shelfLifeBadgeText } from '../../components/stores/labels';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { storeQueryKeys } from './queryKeys';

function StoreRow({ store }: { store: StoreDto & { distanceM: number | null } }) {
  const badge = shelfLifeBadgeText(store);
  return (
    <li>
      <Link
        to={`/stores/${store.id}`}
        className="flex min-h-16 items-center justify-between gap-3 card p-3 hover:border-accent"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate font-medium text-ink">{store.name}</span>
            {badge ? (
              <span className="shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-xs text-brand">
                {badge}
              </span>
            ) : null}
          </span>
          <span className="block truncate text-sm text-muted">
            {store.chain}
            {store.address ? ` · ${store.address}` : ''}
          </span>
          <span className="block truncate text-xs text-muted">
            {departmentOrderSummary(store.departmentOrder)}
          </span>
        </span>
        <FavouriteButton storeId={store.id} favourite={store.favourite} size="sm" />
      </Link>
    </li>
  );
}

export function StoreListPage() {
  const [showAdd, setShowAdd] = useState(false);
  const [qInput, setQInput] = useState('');
  const debouncedQ = useDebouncedValue(qInput, 250);

  const trimmedQ = debouncedQ.trim();
  const stores = useQuery({
    queryKey: storeQueryKeys.list(trimmedQ),
    queryFn: () => api.stores.list({ limit: 100, ...(trimmedQ ? { q: trimmedQ } : {}) }),
  });

  const favourites = stores.data?.stores.filter((s) => s.favourite) ?? [];
  const others = stores.data?.stores.filter((s) => !s.favourite) ?? [];

  return (
    <AppShell title="Stores">
      <div className="flex flex-col gap-4">
        <Section
          title="Add a store"
          description="Name, chain and location — used to sort grocery lists by walking order."
        >
          {showAdd ? (
            <AddStoreForm onAdded={() => setShowAdd(false)} />
          ) : (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="min-h-11 rounded-lg border border-dashed border-line px-4 text-sm font-medium text-brand hover:bg-brand-soft"
            >
              + Add a store
            </button>
          )}
        </Section>

        <div className="flex flex-col gap-1">
          <label htmlFor="store-search" className="text-sm font-medium text-ink">
            Search
          </label>
          <input
            id="store-search"
            type="search"
            value={qInput}
            placeholder="Find a store…"
            onChange={(e) => setQInput(e.target.value)}
            className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
          />
        </div>

        {stores.isPending ? <Skeleton lines={3} /> : null}
        {stores.isError ? <Alert tone="error">{messageForError(stores.error)}</Alert> : null}
        {stores.isSuccess && stores.data.stores.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line bg-surface p-6 text-center text-sm text-muted">
            No stores match yet — add one above.
          </p>
        ) : null}

        {favourites.length > 0 ? (
          <section aria-labelledby="stores-favourites">
            <h2 id="stores-favourites" className="mb-2 text-sm font-semibold text-brand">
              Favourites <span className="font-normal text-muted">({favourites.length})</span>
            </h2>
            <ul className="flex flex-col gap-2">
              {favourites.map((store) => (
                <StoreRow key={store.id} store={store} />
              ))}
            </ul>
          </section>
        ) : null}

        {others.length > 0 ? (
          <section aria-labelledby="stores-others">
            <h2 id="stores-others" className="mb-2 text-sm font-semibold text-brand">
              Other stores <span className="font-normal text-muted">({others.length})</span>
            </h2>
            <ul className="flex flex-col gap-2">
              {others.map((store) => (
                <StoreRow key={store.id} store={store} />
              ))}
            </ul>
          </section>
        ) : null}

        <Section
          title="Default products"
          description="The household's chosen product per ingredient, used to round grocery quantities up to whole packages."
        >
          <DefaultProductsSection />
        </Section>
      </div>
    </AppShell>
  );
}
